import React, { useCallback, useEffect, useState } from "react";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import {
  MdAdd,
  MdEdit,
  MdDelete,
  MdDragIndicator,
  MdAccountBalance,
  MdVisibility,
  MdVisibilityOff,
  MdStar,
  MdStarBorder,
  MdFlag,
} from "react-icons/md";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PageContainer, PageHeader } from "components/page";
import Card, { CardHeader } from "components/card";
import Button, { IconButton } from "components/button";
import Badge from "components/badge";
import Dialog from "components/dialog";
import { useConfirm } from "components/dialog/ConfirmProvider";
import { Checkbox, InputField, Textarea } from "components/fields";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import AlertPane from "components/alert";
import { formatCurrency, formatDate } from "utils/format";

const FUND_FORM_ID = "fund-form";

const formatDateForInput = (date) => {
  if (!date) return "";
  return new Date(date).toISOString().split("T")[0];
};

// goalAmount is stored in cents.
const goalText = (fund) =>
  [
    `Goal: ${formatCurrency(fund.goalAmount / 100)}`,
    fund.goalEndDate && `by ${formatDate(fund.goalEndDate)}`,
  ]
    .filter(Boolean)
    .join(" ");

const SortableFundItem = ({ fund, saving, onEdit, onDelete, onToggleActive, onSetDefault }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: fund.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 border-t border-line bg-surface py-3 first:border-t-0 ${
        isDragging ? "relative z-10 rounded-control shadow-overlay" : ""
      }`}
    >
      <IconButton
        icon={<MdDragIndicator />}
        label={`Reorder “${fund.name}”`}
        className="shrink-0 cursor-grab touch-none text-fg-muted active:cursor-grabbing"
        {...attributes}
        {...listeners}
      />

      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
        <MdAccountBalance aria-hidden="true" className="h-5 w-5" />
      </span>

      <div className={`min-w-0 flex-1 ${fund.isActive ? "" : "opacity-60"}`}>
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-semibold text-fg">{fund.name}</h3>
          {fund.isDefault && (
            <Badge tone="brand">
              <MdStar aria-hidden="true" className="h-3 w-3" />
              Default
            </Badge>
          )}
          {!fund.isActive && <Badge tone="neutral">Inactive</Badge>}
          {Boolean(fund.goalAmount) && (
            <Badge tone="info">
              <MdFlag aria-hidden="true" className="h-3 w-3" />
              {goalText(fund)}
            </Badge>
          )}
        </div>
        {fund.description && <p className="text-sm text-fg-muted">{fund.description}</p>}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {!fund.isDefault && (
          <IconButton
            icon={<MdStarBorder />}
            label={`Set “${fund.name}” as Default`}
            onClick={() => onSetDefault(fund)}
            disabled={saving}
          />
        )}
        <IconButton
          icon={fund.isActive ? <MdVisibility /> : <MdVisibilityOff />}
          label={`${fund.isActive ? "Deactivate" : "Activate"} “${fund.name}”`}
          onClick={() => onToggleActive(fund)}
          disabled={saving}
        />
        <IconButton icon={<MdEdit />} label={`Edit “${fund.name}”`} onClick={() => onEdit(fund)} />
        {!fund.isDefault && (
          <IconButton
            icon={<MdDelete />}
            label={`Delete “${fund.name}”`}
            onClick={() => onDelete(fund)}
            disabled={saving}
          />
        )}
      </div>
    </li>
  );
};

const FundForm = ({ fund, onSave }) => {
  const [formData, setFormData] = useState(() => ({
    name: fund?.name || "",
    description: fund?.description || "",
    goalAmount: fund?.goalAmount ? (fund.goalAmount / 100).toFixed(2) : "",
    goalEndDate: formatDateForInput(fund?.goalEndDate),
    isDefault: fund?.isDefault || false,
  }));

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name: formData.name,
      description: formData.description || null,
      goalAmount: formData.goalAmount ? parseFloat(formData.goalAmount) : null,
      goalEndDate: formData.goalEndDate || null,
      isDefault: formData.isDefault,
    });
  };

  return (
    <form id={FUND_FORM_ID} onSubmit={handleSubmit} className="flex flex-col gap-4">
      <InputField
        label="Fund Name"
        name="name"
        value={formData.name}
        onChange={handleInputChange}
        required
        placeholder="General Fund, Missions, Building Fund"
      />
      <Textarea
        label="Description"
        hint="Optional"
        name="description"
        value={formData.description}
        onChange={handleInputChange}
        placeholder="Brief description of this fund"
        rows={2}
      />
      <fieldset className="flex flex-col gap-4 rounded-control border border-line p-4">
        <legend className="px-1 text-sm font-medium text-fg">Fundraising Goal (Optional)</legend>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <InputField
            label="Goal Amount ($)"
            type="number"
            name="goalAmount"
            value={formData.goalAmount}
            onChange={handleInputChange}
            min="0"
            step="0.01"
            placeholder="10000.00"
          />
          <InputField
            label="Goal End Date"
            type="date"
            name="goalEndDate"
            value={formData.goalEndDate}
            onChange={handleInputChange}
          />
        </div>
      </fieldset>
      {!fund?.isDefault && (
        <Checkbox
          label="Set as Default Fund"
          description="The default fund is pre-selected on the giving form."
          name="isDefault"
          checked={formData.isDefault}
          onChange={handleInputChange}
        />
      )}
    </form>
  );
};

const FundsManagement = () => {
  const confirm = useConfirm();
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFund, setEditingFund] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchFunds = useCallback(async () => {
    setLoadError(null);
    try {
      const response = await AtriumApi.get("/connect/funds?includeInactive=true");
      if (response.data.success) {
        setFunds(response.data.data || []);
      }
    } catch (error) {
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFunds();
  }, [fetchFunds]);

  const retryFetch = () => {
    setLoading(true);
    fetchFunds();
  };

  const updateFundPositions = async (reorderedFunds) => {
    setSaving(true);
    try {
      const fundIds = reorderedFunds.map((fund) => fund.id);
      await AtriumApi.put("/connect/funds/reorder", { fundIds });
      notify.success("Fund order saved");
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not update fund order"));
      // Revert to the server order
      fetchFunds();
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = funds.findIndex((fund) => fund.id === active.id);
    const newIndex = funds.findIndex((fund) => fund.id === over.id);
    const newFunds = arrayMove(funds, oldIndex, newIndex);
    setFunds(newFunds);
    await updateFundPositions(newFunds);
  };

  const handleAddFund = () => {
    setEditingFund(null);
    setModalOpen(true);
  };

  const handleEditFund = (fund) => {
    setEditingFund(fund);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingFund(null);
  };

  const handleSaveFund = async (formData) => {
    setSaving(true);
    try {
      const response = editingFund
        ? await AtriumApi.put(`/connect/funds/${editingFund.id}`, formData)
        : await AtriumApi.post("/connect/funds", formData);

      if (response.data.success) {
        notify.success(editingFund ? "Fund saved" : "Fund created");
        closeModal();
        fetchFunds();
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not save fund"));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFund = async (fund) => {
    const confirmed = await confirm({
      title: `Delete "${fund.name}"?`,
      message: "Donors will no longer be able to give to this fund. Funds with existing donations are deactivated instead.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;

    setSaving(true);
    try {
      const response = await AtriumApi.delete(`/connect/funds/${fund.id}`);
      if (response.data.success) {
        notify.success(response.data.message || "Fund deleted");
        fetchFunds();
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not delete fund"));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (fund) => {
    setSaving(true);
    try {
      const response = await AtriumApi.put(`/connect/funds/${fund.id}`, {
        isActive: !fund.isActive,
      });
      if (response.data.success) {
        notify.success(fund.isActive ? "Fund deactivated" : "Fund activated");
        fetchFunds();
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not update fund"));
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (fund) => {
    setSaving(true);
    try {
      const response = await AtriumApi.put(`/connect/funds/${fund.id}/default`);
      if (response.data.success) {
        notify.success(`"${fund.name}" is now the default fund`);
        fetchFunds();
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not set the default fund"));
    } finally {
      setSaving(false);
    }
  };

  const addButton = (
    <Button leftIcon={<MdAdd />} onClick={handleAddFund} disabled={saving}>
      Add Fund
    </Button>
  );

  const renderFunds = () => {
    if (loadError) {
      return (
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(loadError, "Could not load funds")}
            onRetry={retryFetch}
          />
        </Card>
      );
    }
    if (loading) {
      return (
        <Card padding="md" className="gap-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </Card>
      );
    }
    if (funds.length === 0) {
      return (
        <Card padding="md">
          <EmptyState
            icon={MdAccountBalance}
            title="No Funds Yet"
            description="Add your first giving fund to get started."
            action={addButton}
          />
        </Card>
      );
    }
    return (
      <Card padding="md">
        <CardHeader
          title="Funds"
          description={`${funds.length} ${funds.length === 1 ? "fund" : "funds"} total. Drag to reorder.`}
        />
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={funds.map((fund) => fund.id)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="mt-4">
              {funds.map((fund) => (
                <SortableFundItem
                  key={fund.id}
                  fund={fund}
                  saving={saving}
                  onEdit={handleEditFund}
                  onDelete={handleDeleteFund}
                  onToggleActive={handleToggleActive}
                  onSetDefault={handleSetDefault}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      </Card>
    );
  };

  return (
    <PageContainer>
      <PageHeader
        title="Giving Funds"
        description="Create and manage donation fund categories"
        actions={!loadError && addButton}
      />

      {renderFunds()}

      <AlertPane tone="info" title="About Giving Funds" className="">
        <ul className="list-inside list-disc space-y-1">
          <li>Funds allow donors to designate where their gifts go</li>
          <li>The default fund is pre-selected on the giving form</li>
          <li>You can set fundraising goals with target amounts and dates</li>
          <li>Deactivated funds won't appear on the public giving page</li>
          <li>Funds with existing donations can only be deactivated, not deleted</li>
        </ul>
      </AlertPane>

      <Dialog
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingFund ? "Edit Fund" : "Add Fund"}
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" form={FUND_FORM_ID} loading={saving}>
              {editingFund ? "Save Changes" : "Create Fund"}
            </Button>
          </>
        }
      >
        <FundForm fund={editingFund} onSave={handleSaveFund} />
      </Dialog>
    </PageContainer>
  );
};

export default FundsManagement;
